import { z } from 'zod';
/**
 * Fleet operations summary — the dashboard's figures.
 *
 * The dashboard previously answered only "how many are there", with no time
 * dimension at all: every count was all-time, so "is this week worse than last
 * week" — the question an operations manager actually opens a dashboard to ask
 * — could not be answered from it. This adds the period, and the previous
 * period beside it.
 *
 * Three refusals shape the design, and each exists because the obvious version
 * would mislead:
 *
 *  - **No change figure against a partial previous period.** A tenant that has
 *    existed for four days has no fortnight to compare a week against, and
 *    "+300%" derived from a window that did not exist is a fabrication. The
 *    comparison is withheld and the reason given.
 *  - **Vehicle counts are not normalised by usage**, because the platform has
 *    no reliable per-vehicle utilisation figure to normalise by. A vehicle that
 *    runs double shifts will top the list without being worse maintained. The
 *    field is called `mostIncidents`, never "worst", and the page says so.
 *  - **Zero incidents is not health.** A vehicle sending no telemetry raises no
 *    incidents and looks perfect. `silentVehicles` is reported alongside, so a
 *    quiet fleet and a healthy one are distinguishable — the same distinction
 *    the CAN pages already draw between stale and never-reported.
 */
/** Selectable windows. Hours rather than days so "today" is expressible. */
export const FLEET_SUMMARY_PERIODS = [
    { value: 24, label: 'Last 24 hours' },
    { value: 24 * 7, label: 'Last 7 days' },
    { value: 24 * 30, label: 'Last 30 days' },
    { value: 24 * 90, label: 'Last 90 days' },
];
export const FLEET_SUMMARY_DEFAULT_HOURS = 24 * 7;
export const FLEET_SUMMARY_MIN_HOURS = 24;
export const FLEET_SUMMARY_MAX_HOURS = 24 * 90;
/** Vehicles listed in the per-vehicle breakdown. */
export const FLEET_SUMMARY_VEHICLE_LIMIT = 10;
/**
 * A vehicle is counted as silent when it has sent no telemetry for this long.
 *
 * Matches `FLEET_STALE_AFTER_HOURS` so the dashboard and the CAN fleet page
 * cannot disagree about which vehicles are quiet.
 */
export const FLEET_SUMMARY_SILENT_AFTER_HOURS = 24;
/**
 * The window, either trailing or closed.
 *
 * `hours` alone means "the last N hours from now" — reproducible only by accident,
 * which is why a scheduled report cannot use it (scheduled-reports design D4). A
 * report for "21–27 July" needs to name the period, so `from`/`to` are accepted and
 * `hours` becomes sugar that resolves to them.
 *
 * Both forms funnel through `resolveFleetSummaryWindow` so the service only ever
 * sees a closed period. One code path, which is what keeps a report and the
 * dashboard agreeing by construction rather than by review.
 */
export const fleetSummaryQuerySchema = z
    .object({
    hours: z.coerce
        .number()
        .int()
        .min(FLEET_SUMMARY_MIN_HOURS)
        .max(FLEET_SUMMARY_MAX_HOURS)
        .optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const bounded = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if ((v.from === undefined) !== (v.to === undefined)) {
        // Half a period is not a period. Defaulting the other end would invent a
        // window the caller did not ask for.
        bounded(v.from === undefined ? 'from' : 'to', 'Give both from and to, or neither.');
        return;
    }
    if (v.from === undefined || v.to === undefined)
        return;
    if (v.hours !== undefined) {
        /*
         * Refused rather than resolved by precedence. Silently ignoring one of two
         * conflicting windows is how a report ends up covering something nobody
         * asked for, and the caller cannot tell which won.
         */
        bounded('hours', 'Use hours or from/to, not both.');
        return;
    }
    if (v.from.getTime() >= v.to.getTime()) {
        bounded('from', 'from must be before to.');
        return;
    }
    const hours = (v.to.getTime() - v.from.getTime()) / HOUR_MS;
    if (hours < FLEET_SUMMARY_MIN_HOURS || hours > FLEET_SUMMARY_MAX_HOURS) {
        bounded('to', `The period must be between ${FLEET_SUMMARY_MIN_HOURS} and ${FLEET_SUMMARY_MAX_HOURS} hours.`);
    }
});
const HOUR_MS = 60 * 60 * 1000;
/** How the window was arrived at, which decides what can honestly be reported. */
export const periodKindSchema = z.enum(['TRAILING', 'CLOSED']);
/**
 * Resolve either form to one closed window.
 *
 * `now` is a parameter rather than read inside, so the result is a function of its
 * inputs. A report regenerated for the same period must produce the same window, and
 * a hidden `new Date()` is exactly what would stop it.
 *
 * A future `to` is clamped rather than refused: a schedule that fires a second early,
 * or a clock a little ahead, should not fail — but the window must not extend past
 * the present, because a partial period compared against a full one reads as a
 * collapse in activity.
 */
export function resolveFleetSummaryWindow(query, now) {
    if (query.from !== undefined && query.to !== undefined) {
        const to = query.to.getTime() > now.getTime() ? now : query.to;
        const hours = (to.getTime() - query.from.getTime()) / HOUR_MS;
        return {
            from: query.from,
            to,
            previousFrom: new Date(query.from.getTime() - (to.getTime() - query.from.getTime())),
            hours,
            kind: 'CLOSED',
        };
    }
    const hours = query.hours ?? FLEET_SUMMARY_DEFAULT_HOURS;
    const from = new Date(now.getTime() - hours * HOUR_MS);
    return {
        from,
        to: now,
        previousFrom: new Date(from.getTime() - hours * HOUR_MS),
        hours,
        kind: 'TRAILING',
    };
}
/**
 * Why a point-in-time count is not reported.
 *
 * `openNow` and friends are answered from the incident `status` column, which holds
 * only the CURRENT state — the platform keeps no status history. For a trailing
 * window ending now that is exactly right. For a closed past period it would report
 * today's open count in a summary of last week, which is not a smaller answer but a
 * wrong one.
 *
 * Withheld with a reason, following the same rule this endpoint already applies to
 * period-on-period change: an invented figure always reads as information.
 *
 * Deriving it from `openedAt`/`resolvedAt` was considered and rejected — it would be
 * a second source of truth that disagrees with `status` for anything dismissed or
 * reopened. If a report genuinely needs "open at period end", the honest fix is
 * incident status history in the product, not an approximation here.
 */
export const pointInTimeUnavailableSchema = z.enum(['HISTORICAL_PERIOD']);
/** Why a period-on-period change is not offered. */
export const changeUnavailableSchema = z.enum(['NO_PREVIOUS_PERIOD', 'PREVIOUS_PERIOD_EMPTY']);
/**
 * A count with its previous-period counterpart.
 *
 * `changePct` is null whenever the comparison would be dishonest rather than
 * merely zero, and `unavailable` says which case it is. A dash the reader
 * interprets as "no change" is the failure this avoids.
 */
export const periodCountSchema = z.object({
    count: z.number().int(),
    previous: z.number().int().nullable(),
    changePct: z.number().nullable(),
    unavailable: changeUnavailableSchema.nullable(),
});
export const fleetSummaryVehicleSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    incidents: z.number().int(),
    critical: z.number().int(),
    /**
     * Open right now, of those raised in the period.
     *
     * Point-in-time, so it is WITHHELD for a closed period exactly as `openNow` is
     * (D4a): it is answered from the current incident status, and reporting it against
     * last week would print today's number under last week's heading.
     */
    stillOpen: z.number().int().nullable(),
    previous: z.number().int(),
    /**
     * Which branch this vehicle belongs to — CONSOLIDATED reads only.
     *
     * ⚠️ A merged ranking that does not say where each row came from is a list
     * the reader cannot act on. Five vehicles from three branches look like one
     * fleet, and "go and look at Tipper 301" is a different instruction
     * depending on whose depot it is in. The same rule the copilot's
     * `CONCATENABLE` capabilities already follow: append with a branch column.
     *
     * NULL for a single-tenant read, deliberately — naming the branch you are
     * already standing in is noise, and a column that is the same value on every
     * row teaches a reader to stop looking at it.
     */
    branch: z
        .object({ tenantId: z.string().uuid(), tenantName: z.string() })
        .nullable()
        .default(null),
});
/** One day of the series. `date` is a UTC `YYYY-MM-DD`. */
export const fleetSummaryDaySchema = z.object({
    date: z.string(),
    opened: z.number().int(),
    resolved: z.number().int(),
});
export const fleetSummaryResponseSchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    /** Length of the reported window. Fractional when a closed period is not whole. */
    hours: z.number(),
    kind: periodKindSchema,
    previousFrom: z.string().datetime(),
    /** False when the tenant is younger than the comparison window. */
    previousPeriodComplete: z.boolean(),
    /** Raised in the period, against the period before it. */
    opened: periodCountSchema,
    resolved: periodCountSchema,
    /**
     * Open at this moment, regardless of when they were raised.
     *
     * Point-in-time, so it carries no previous-period comparison: an inventory
     * and a flow are different quantities and must not be shown as one figure.
     */
    openNow: z.number().int().nullable(),
    criticalOpenNow: z.number().int().nullable(),
    /** Awaiting a human decision right now. */
    awaitingApproval: z.number().int().nullable(),
    /**
     * Recommendations nobody has decided on yet — the queue, as a number.
     *
     * ⚠️ **WIDER THAN `awaitingApproval`, which is why both exist.** That one counts only advice
     * needing a PRIVILEGED decision (`approvalRequired`), so a recommendation an operator can
     * simply acknowledge is invisible to it while still sitting in somebody's queue. Reported
     * from the field: a queue of three drew a menu badge of 1, and the row it dropped was `New`.
     *
     * ⚠️ **`GENERATED` only, mirroring `openNow` exactly.** An `ACKNOWLEDGED` recommendation is
     * still live — it can reach `ACTIONED` or `DISMISSED` — and is deliberately NOT counted, for
     * the reason `openNow` counts only status `OPEN`: somebody has picked it up, and a figure
     * that goes on nagging about work already in hand is one people learn to skip.
     * `vehiclesRequiringAttention` is where the whole active set is counted, and the two disagree
     * by design. `SUPERSEDED` is excluded because it is advice the platform WITHDREW: it appears
     * in no transition's `from` list, so no decision on it is possible.
     */
    undecidedRecommendations: z.number().int().nullable(),
    /**
     * Active vehicles with an unresolved incident or an outstanding recommendation.
     *
     * A UNION counted once per vehicle, not the sum of two counts — most vehicles
     * needing attention have both, and adding them would report more vehicles than
     * the fleet has. Point-in-time for the same reason as `openNow`, and withheld
     * alongside it: it is answered from the CURRENT status of incidents and
     * recommendations, so a closed past period would be told today's backlog.
     *
     * Deliberately wider than `openNow`, which counts only incidents in status
     * `OPEN`. An incident that has been acknowledged or picked up still describes
     * a vehicle that needs attending to, so this uses the whole active set. The
     * two figures therefore disagree by design, and the screen says which is which.
     */
    vehiclesRequiringAttention: z.number().int().nullable(),
    /** Set when the four counts above are withheld; null when they are reported. */
    pointInTimeUnavailable: pointInTimeUnavailableSchema.nullable(),
    byRisk: z.array(z.object({ level: z.string(), count: z.number().int(), previous: z.number().int() })),
    byType: z.array(z.object({ type: z.string(), count: z.number().int(), previous: z.number().int() })),
    /**
     * Median hours from opening to resolution, for incidents RESOLVED in the
     * period. Null when too few resolved to have a middle value.
     */
    medianResolutionHours: z.number().nullable(),
    resolvedSampleSize: z.number().int(),
    vehiclesActive: z.number().int(),
    vehiclesWithIncidents: z.number().int(),
    /**
     * Active vehicles that have sent no telemetry recently.
     *
     * Reported beside the incident counts because they are the reason a clean
     * dashboard may not mean a clean fleet: a vehicle that reports nothing
     * raises nothing.
     */
    silentVehicles: z.number().int(),
    silentAfterHours: z.number().int(),
    mostIncidents: z.array(fleetSummaryVehicleSchema),
    daily: z.array(fleetSummaryDaySchema),
});
// ── consolidated across sub-tenants (tenant-hierarchy-design D6–D8a) ────────
/**
 * How a figure survives being combined across sub-tenants.
 *
 * Declared per field rather than decided per call site, because the failure this
 * prevents is silent: a consolidated median that is quietly the mean of medians
 * is wrong in a way nobody can see, and it looks exactly like a correct one.
 *
 * - `SUM` — counts add. Safe because a vehicle, an incident and a person each
 *   belong to exactly one sub-tenant, so nothing is counted twice.
 * - `WEIGHTED_MEAN` — an average that can be reconstructed from a total and a
 *   weight. Declared because the vocabulary needs it; nothing uses it yet.
 * - `RECOMPUTE` — derived from figures that DO merge, and recalculated from the
 *   merged totals rather than combined itself.
 * - `IDENTICAL` — not aggregated at all: the same value for every sub-tenant,
 *   asserted rather than assumed. The reported window is the obvious case.
 * - `NOT_AGGREGATABLE` — withheld, with a reason. Medians, rates and rankings
 *   that cannot be reconstructed from what each part returned.
 */
export const MERGE_RULES = [
    'SUM',
    'WEIGHTED_MEAN',
    'RECOMPUTE',
    'IDENTICAL',
    'NOT_AGGREGATABLE',
];
export const mergeRuleSchema = z.enum(MERGE_RULES);
export const withheldFigureSchema = z.object({
    field: z.string(),
    rule: mergeRuleSchema,
    /** Said in words, because "—" on a dashboard reads as zero. */
    reason: z.string(),
});
/**
 * A fleet summary combined across every sub-tenant of a parent.
 *
 * The same shape as a single tenant's, so one component renders both — plus what
 * it is made of and what had to be withheld. A figure that cannot be merged is
 * NULL here AND named in `withheld`; a client that ignored `withheld` would show
 * a blank rather than a wrong number, which is the safe direction to fail.
 */
export const consolidatedFleetSummaryResponseSchema = fleetSummaryResponseSchema.extend({
    /** Which sub-tenants this is made of, in the order they were gathered. */
    tenants: z.array(z.object({ tenantId: z.string().uuid(), tenantName: z.string() })),
    withheld: z.array(withheldFigureSchema),
});
//# sourceMappingURL=fleet-summary.js.map